import type { MatterEvent } from "@/lib/types";
import isEqual from "lodash/isEqual";

export type EventWithType = MatterEvent & {
  type: string;
  itemId: string;
};

type JsonRecord = Record<string, unknown>;

export type EventLogDiff = {
  field: string;
  before?: unknown;
  after?: unknown;
};

const isJsonRecord = (value: unknown): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const parseEventLogData = (
  data?: string | JsonRecord | null,
): JsonRecord | undefined => {
  if (!data) {
    return undefined;
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(trimmed);
      return isJsonRecord(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  if (isJsonRecord(data)) {
    return data;
  }

  return undefined;
};

export const getEventLogDiff = (event: MatterEvent): EventLogDiff[] => {
  return Object.entries(event.changes ?? {}).flatMap(([field, change]) =>
    isEqual(change.from, change.to)
      ? []
      : [{ field, before: change.from, after: change.to }],
  );
};

export const getEventLogDiffFromData = (
  current?: JsonRecord,
  previous?: JsonRecord,
): EventLogDiff[] => {
  if (!current && !previous) {
    return [];
  }

  const fields = new Set([
    ...(current ? Object.keys(current) : []),
    ...(previous ? Object.keys(previous) : []),
  ]);

  const diffs: EventLogDiff[] = [];
  fields.forEach((field) => {
    const before = previous?.[field];
    const after = current?.[field];
    if (isEqual(before, after)) {
      return;
    }

    diffs.push({ field, before, after });
  });

  return diffs;
};

export const formatEventWithType = ({ event }: { event: MatterEvent }) => {
  const subjectTypeLabels: Record<string, string> = {
    incident_report: "Incident Report",
    matter_email: "Email",
    matter_expense: "Cost",
    matter_file: "File",
    matter_insurance: "Insurance",
    matter_lien: "Lien",
    matter_litigation_date: "Litigation Date",
    matter_lost_wages: "Lost Wage",
    matter_note: "Note",
    matter_property_damage: "Property Damage",
    matter_settlement_counter: "Settlement Counter",
    matter_settlement_demand: "Settlement Demand",
    matter_settlement_offer: "Settlement Offer",
    matter_task: "Task",
    matter_trust_check: "Trust Check",
    matter_vehicle: "Vehicle",
    medical_treatment: "Medical Treatment",
    medical_treatment_appointment: "Medical Treatment Appointment",
    medical_treatment_balance_confirmation: "Balance Confirmation",
    medical_treatment_bill: "Medical Treatment Bill",
    medical_treatment_plan: "Medical Treatment Plan",
    medical_treatment_record: "Medical Treatment Record",
  };
  const type =
    (event.primarySubjectType && subjectTypeLabels[event.primarySubjectType]) ??
    "Matter";
  const itemId = event.primarySubjectId ?? event.matterId;

  return {
    ...event,
    type,
    itemId,
  };
};
